import { useEffect, useLayoutEffect, useState } from 'react';
import { Appearance, Platform } from 'react-native';
import type { ThemeMode } from '../lib/theme-mode';

const useAppearanceEffect =
  Platform.OS === 'web' && typeof window === 'undefined' ? useEffect : useLayoutEffect;

let lastNativeScheme: 'light' | 'dark' | undefined;

function readColorScheme() {
  const scheme = Appearance.getColorScheme();
  if (Platform.OS === 'web') return scheme;
  if (scheme === 'light' || scheme === 'dark') lastNativeScheme = scheme;
  // A provider remount can also encounter RN's cached `unspecified` value.
  return lastNativeScheme;
}

export function useAppColorScheme(themeMode: ThemeMode): 'light' | 'dark' {
  const [nativeScheme, setNativeScheme] = useState(readColorScheme);

  useAppearanceEffect(() => {
    const subscription = Appearance.addChangeListener(({ colorScheme }) => {
      if (colorScheme === 'light' || colorScheme === 'dark') {
        if (Platform.OS !== 'web') lastNativeScheme = colorScheme;
        setNativeScheme(colorScheme);
      }
    });
    return () => subscription.remove();
  }, []);

  useAppearanceEffect(() => {
    if (Platform.OS === 'web') return;
    // Release the override in System mode so scheduled OS changes still work.
    // RN 0.83 caches `unspecified` here, even when UIKit's appearance stays the
    // same and emits no change. Keep the last actual appearance event above
    // instead of reading that transient value back through useColorScheme.
    Appearance.setColorScheme(themeMode === 'system' ? 'unspecified' : themeMode);
  }, [themeMode]);

  return themeMode === 'system'
    ? nativeScheme === 'dark' ? 'dark' : 'light'
    : themeMode;
}
